import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from "src/@/components/ui/button";
import { Input } from 'src/@/components/ui/input';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from 'src/@/components/ui/table';
import { Checkbox } from 'src/@/components/ui/checkbox';
import Scrollbar from 'src/components/Scrollbar';
import tarefasService from 'src/services/tarefasService';
import { actionCriarWorkflow } from 'src/actions/tarefas/actionTarefasConfig';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "src/@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/@/components/ui/select";
import { getListaUsuariosByClienteId, getListaSetoresTransferencia } from 'src/redux/slices/user';
import TarefaDialog from './TarefaDialog';
import AlertasTarefasDialog from './AlertasTarefasDialog';
import { AT } from './AlertasTarefas.design';
import KanbanBoard from './KanbanBoard';
import CalendarView from './CalendarView';
import { usePermissaoNotificacao } from 'src/hooks/useNotificacoes';
import { TAREFAS_DESIGN } from './Tarefas.design';

const P = TAREFAS_DESIGN.page;

const PRIORITY_CONFIG = {
  low: { label: 'Baixa', bg: 'bg-gray-100 dark:bg-gray-500/10', text: 'text-gray-600 dark:text-gray-400' },
  medium: { label: 'Média', bg: 'bg-amber-100 dark:bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400' },
  high: { label: 'Alta', bg: 'bg-orange-100 dark:bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400' },
  urgent: { label: 'Urgente', bg: 'bg-red-100 dark:bg-red-500/10', text: 'text-red-700 dark:text-red-400' },
};

export default function Tarefas() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { usuariosList, listaSetoresTransferencia } = useSelector((state) => state.user);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  // Prazo pré-preenchido ao criar tarefa clicando num dia do calendário.
  const [newTaskDueDate, setNewTaskDueDate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [alertasOpen, setAlertasOpen] = useState(false);
  const [avisoAlertas, setAvisoAlertas] = useState(() => {
    try {
      return localStorage.getItem('lais_tarefas_aviso_alertas') !== 'visto';
    } catch {
      return false; // sem localStorage, não insistir a cada carga
    }
  });

  const dispensarAvisoAlertas = () => {
    try {
      localStorage.setItem('lais_tarefas_aviso_alertas', 'visto');
    } catch {
      // sem localStorage a faixa volta no próximo load; aceitável
    }
    setAvisoAlertas(false);
  };
  // Sem permissão do navegador o alerta só aparece no sino — o ponto no botão
  // sinaliza que há algo a ativar, em vez de o usuário descobrir na falta.
  const { estado: permissaoNotificacao } = usePermissaoNotificacao();
  const [selectedIds, setSelectedIds] = useState([]);

  // No workflow dialog
  const [noWorkflowDialogOpen, setNoWorkflowDialogOpen] = useState(false);
  const [creatingDefaultWorkflow, setCreatingDefaultWorkflow] = useState(false);

  // Workflows
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');

  // Filters
  const [filterResponsavel, setFilterResponsavel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPrioridade, setFilterPrioridade] = useState('all');
  const [filterSetor, setFilterSetor] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const loadTasksReqId = useRef(0);

  // Deep-link vindo do sino / toast: /dashboard/tarefas?tarefa=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  const tarefaParam = searchParams.get('tarefa');
  const deepLinkHandled = useRef(null);

  useEffect(() => {
    if (user?.clienteId) {
      loadWorkflows();
      if (usuariosList.length === 0) dispatch(getListaUsuariosByClienteId(user.clienteId));
      if (listaSetoresTransferencia.length === 0) dispatch(getListaSetoresTransferencia(user.clienteId));
    }
  }, [user?.clienteId]);

  const loadWorkflows = async () => {
    try {
      const response = await tarefasService.listarWorkflows(user?.clienteId);
      const wfs = response.data || [];
      setWorkflows(wfs);
      const stillExists = wfs.some(w => w._id === selectedWorkflowId);
      if (wfs.length > 0 && (!selectedWorkflowId || !stillExists)) {
        setSelectedWorkflowId(wfs[0]._id);
      } else if (wfs.length === 0 && selectedWorkflowId) {
        setSelectedWorkflowId('');
      }
    } catch (error) {
      console.error('Erro ao carregar workflows:', error);
    }
  };

  useEffect(() => {
    if (user?.clienteId && selectedWorkflowId) {
      loadTasks();
    }
    setSelectedIds([]);
    setPage(1);
  }, [user?.clienteId, selectedWorkflowId]);

  const selectedWorkflow = useMemo(() =>
    workflows.find(w => w._id === selectedWorkflowId),
    [workflows, selectedWorkflowId]
  );

  const sortedEstagios = useMemo(() => {
    if (!selectedWorkflow?.estagios) return [];
    return [...selectedWorkflow.estagios].sort((a, b) => a.ordem - b.ordem);
  }, [selectedWorkflow]);

  const loadTasks = async () => {
    const reqId = ++loadTasksReqId.current;
    try {
      setLoading(true);
      const params = {};
      if (selectedWorkflowId) params.workflow_id = selectedWorkflowId;
      const response = await tarefasService.listar(user?.clienteId, params);
      if (reqId !== loadTasksReqId.current) return;
      if (!response.error) {
        setTasks(response.data || []);
      }
    } catch (error) {
      if (reqId !== loadTasksReqId.current) return;
      console.error('Erro ao carregar tarefas:', error);
      toast({ title: "Erro", description: "Não foi possível carregar as tarefas", variant: "destructive" });
    } finally {
      if (reqId === loadTasksReqId.current) setLoading(false);
    }
  };

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    let result = tasks;
    const trimmed = searchTerm.trim().toLowerCase();
    if (trimmed) result = result.filter(t => t.title?.toLowerCase().includes(trimmed));
    if (filterResponsavel !== 'all') result = result.filter(t => (t.assigned_to?._id || t.assigned_to) === filterResponsavel);
    if (filterStatus !== 'all') result = result.filter(t => t.status === filterStatus);
    if (filterPrioridade !== 'all') result = result.filter(t => t.priority === filterPrioridade);
    if (filterSetor !== 'all') result = result.filter(t => (t.sector_id?._id || t.sector_id) === filterSetor);
    return result;
  }, [tasks, searchTerm, filterResponsavel, filterStatus, filterPrioridade, filterSetor]);

  // Pagination
  const totalPages = Math.ceil(filteredTasks.length / perPage);
  const paginatedTasks = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredTasks.slice(start, start + perPage);
  }, [filteredTasks, page, perPage]);

  useEffect(() => { setPage(1); }, [searchTerm, filterResponsavel, filterStatus, filterPrioridade, filterSetor]);

  // Active filters
  const activeFilters = useMemo(() => {
    const filters = [];
    if (filterResponsavel !== 'all') {
      const u = usuariosList.find(u => u._id === filterResponsavel);
      filters.push({ key: 'responsavel', label: u?.nomeFantasia || u?.nome || u?.email || 'Responsável', icon: 'mdi:account-outline', clear: () => setFilterResponsavel('all') });
    }
    if (filterStatus !== 'all') {
      filters.push({ key: 'status', label: filterStatus, icon: 'mdi:circle-outline', clear: () => setFilterStatus('all') });
    }
    if (filterPrioridade !== 'all') {
      const p = PRIORITY_CONFIG[filterPrioridade];
      filters.push({ key: 'prioridade', label: p?.label || filterPrioridade, icon: 'mdi:flag-outline', clear: () => setFilterPrioridade('all') });
    }
    if (filterSetor !== 'all') {
      const s = listaSetoresTransferencia.find(s => s._id === filterSetor);
      filters.push({ key: 'setor', label: s?.nome_setor || 'Setor', icon: 'mdi:account-group-outline', clear: () => setFilterSetor('all') });
    }
    return filters;
  }, [filterResponsavel, filterStatus, filterPrioridade, filterSetor, usuariosList, listaSetoresTransferencia]);

  const handleSave = async (data) => {
    try {
      if (editingTask) {
        const response = await tarefasService.editar(editingTask._id, data);
        if (response.error) {
          toast({ title: "Erro", description: response.message || "Não foi possível atualizar a tarefa", variant: "destructive" });
          return null;
        }
        setTasks(prev => prev.map(t => t._id === editingTask._id ? response.data : t));
        toast({ title: "Sucesso", description: "Tarefa atualizada" });
        return response.data;
      }
      const response = await tarefasService.criar(user?.clienteId, data);
      if (response.error) {
        toast({ title: "Erro", description: response.message || "Não foi possível criar a tarefa", variant: "destructive" });
        return null;
      }
      setTasks(prev => [response.data, ...prev]);
      toast({ title: "Sucesso", description: "Tarefa criada" });
      return response.data;
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
      toast({ title: "Erro", description: "Não foi possível salvar a tarefa", variant: "destructive" });
      return null;
    }
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      const response = await tarefasService.deletar(taskToDelete._id);
      if (!response.error) {
        setTasks(prev => prev.filter(t => t._id !== taskToDelete._id));
        setSelectedIds(prev => prev.filter(id => id !== taskToDelete._id));
        toast({ title: "Sucesso", description: "Tarefa deletada" });
      } else {
        toast({ title: "Erro", description: response.message || "Não foi possível deletar a tarefa", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erro", description: "Não foi possível deletar a tarefa", variant: "destructive" });
    } finally {
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    let previousStatus;
    setTasks(prev => prev.map(t => {
      if (t._id !== taskId) return t;
      previousStatus = t.status;
      return { ...t, status: newStatus };
    }));
    const revert = () => {
      if (previousStatus === undefined) return;
      setTasks(prev => prev.map(t => t._id === taskId ? { ...t, status: previousStatus } : t));
    };
    try {
      const response = await tarefasService.atualizarStatus(taskId, { status: newStatus, userId: user?._id || user?.id });
      if (response.error) {
        revert();
        toast({ title: "Erro", description: response.message || "Não foi possível atualizar o status", variant: "destructive" });
      }
    } catch (error) {
      revert();
      toast({ title: "Erro", description: "Não foi possível atualizar o status", variant: "destructive" });
    }
  };

  const handleTaskUpdate = async (taskId, patch) => {
    const previous = tasks.find(t => t._id === taskId);
    if (!previous) return;

    setTasks(prev => prev.map(t => {
      if (t._id !== taskId) return t;
      const next = { ...t, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'assigned_to')) {
        if (!patch.assigned_to) {
          next.assigned_to = null;
        } else {
          const u = usuariosList.find(userItem => userItem._id === patch.assigned_to);
          next.assigned_to = u
            ? { _id: u._id, nome: u.nome, nomeFantasia: u.nomeFantasia, email: u.email }
            : patch.assigned_to;
        }
      }
      return next;
    }));

    try {
      const response = await tarefasService.editar(taskId, patch);
      if (response.error) {
        setTasks(prev => prev.map(t => t._id === taskId ? previous : t));
        toast({ title: "Erro", description: response.message || "Não foi possível atualizar a tarefa", variant: "destructive" });
        return;
      }
      if (response.data) {
        setTasks(prev => prev.map(t => t._id === taskId ? response.data : t));
      }
    } catch (error) {
      setTasks(prev => prev.map(t => t._id === taskId ? previous : t));
      toast({ title: "Erro", description: "Não foi possível atualizar a tarefa", variant: "destructive" });
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  // Abre a tarefa do deep-link assim que a lista carregar. Se ela estiver em
  // outro workflow, troca o workflow (o efeito de loadTasks recarrega e este
  // efeito roda de novo); se nao existir mais, avisa e limpa o parametro.
  useEffect(() => {
    if (!tarefaParam) {
      deepLinkHandled.current = null;
      return;
    }
    if (loading || !user?.clienteId || deepLinkHandled.current === tarefaParam) return;

    const limparParam = () => setSearchParams({}, { replace: true });

    const local = tasks.find(t => t._id === tarefaParam);
    if (local) {
      deepLinkHandled.current = tarefaParam;
      handleEdit(local);
      limparParam();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await tarefasService.buscar(tarefaParam);
        if (cancelled) return;
        if (response?.error || !response?.data) throw new Error(response?.message || 'not found');
        const data = response.data;
        const wf = data.workflow_id?._id || data.workflow_id || '';
        if (wf && wf !== selectedWorkflowId && workflows.some(w => w._id === wf)) {
          setSelectedWorkflowId(wf);
          return; // reload → este efeito encontra a tarefa em `tasks`
        }
        deepLinkHandled.current = tarefaParam;
        handleEdit(data);
        limparParam();
      } catch (err) {
        if (cancelled) return;
        deepLinkHandled.current = tarefaParam;
        toast({ title: 'Tarefa não encontrada', description: 'Ela pode ter sido excluída ou você não tem acesso.', variant: 'destructive' });
        limparParam();
      }
    })();
    return () => { cancelled = true; };
  }, [tarefaParam, tasks, loading, selectedWorkflowId, workflows, user?.clienteId]);

  // Socket 'atualizar_tarefas' (initIO) → recarrega o board de quem esta olhando.
  const refreshTimerRef = useRef(null);
  useEffect(() => {
    if (!user?.clienteId || !selectedWorkflowId) return undefined;
    const handler = () => {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => loadTasks(), 500);
    };
    window.addEventListener('lais:tarefas-atualizadas', handler);
    return () => {
      window.removeEventListener('lais:tarefas-atualizadas', handler);
      clearTimeout(refreshTimerRef.current);
    };
  }, [user?.clienteId, selectedWorkflowId]);

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const isOverdue = (task) => {
    if (!task.due_date) return false;
    const dueDate = new Date(task.due_date);
    const now = new Date();
    const isDone = isTaskDone(task);
    return dueDate < now && !isDone;
  };

  const isTaskDone = (task) => {
    if (selectedWorkflow?.estagios) {
      const sorted = [...selectedWorkflow.estagios].sort((a, b) => a.ordem - b.ordem);
      const lastStage = sorted[sorted.length - 1];
      return lastStage && task.status === lastStage.nome;
    }
    return task.status === 'done' || task.status === 'Concluído';
  };

  const renderStatusBadge = (task) => {
    const overdue = isOverdue(task);
    if (overdue) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400">
          Atrasada
        </span>
      );
    }
    const estagio = sortedEstagios.find(e => e.nome === task.status);
    if (estagio) {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md"
          style={{ backgroundColor: `${estagio.cor}18`, color: estagio.cor }}
        >
          {estagio.nome}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md bg-gray-100 text-gray-600 dark:bg-gray-500/15 dark:text-gray-400">
        {task.status}
      </span>
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const allCurrentPageSelected = paginatedTasks.length > 0 && paginatedTasks.every(t => selectedIds.includes(t._id));

  const toggleSelectAll = () => {
    const pageIds = paginatedTasks.map(t => t._id);
    if (allCurrentPageSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const clearAllFilters = () => {
    setFilterResponsavel('all');
    setFilterStatus('all');
    setFilterPrioridade('all');
    setFilterSetor('all');
    setSearchTerm('');
  };

  const handleNovaTarefa = () => {
    if (workflows.length === 0) {
      setNoWorkflowDialogOpen(true);
      return;
    }
    setEditingTask(null);
    setNewTaskDueDate(null);
    setDialogOpen(true);
  };

  const handleNovaTarefaNaData = (date) => {
    if (workflows.length === 0) {
      setNoWorkflowDialogOpen(true);
      return;
    }
    // 09:00 como horário padrão: meia-noite faria a tarefa nascer "atrasada"
    // assim que o dia começasse.
    const due = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0);
    setEditingTask(null);
    setNewTaskDueDate(due);
    setDialogOpen(true);
  };

  return (
    <div className={P.root} style={{ fontFamily: P.fontStack }}>
      <div className={P.stickyHeader}>
        <div className={P.container}>
          <div className={P.headerRow}>
            <h1 className={P.title}>Tarefas</h1>

            <div className={P.searchRow}>
              <div className={P.searchField}>
                <Input
                  className={P.searchInput}
                  placeholder="Buscar tarefas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className={P.searchIcon}>
                  <Iconify icon="eva:search-outline" width={18} />
                </span>
              </div>
            </div>

            <div className={P.topbarActions}>
              <div className={P.viewToggle}>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? P.iconButtonActive : P.iconButton}
                  title="Lista"
                  aria-label="Visualização em lista"
                >
                  <Iconify icon="mdi:format-list-bulleted" width={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('kanban')}
                  className={viewMode === 'kanban' ? P.iconButtonActive : P.iconButton}
                  title="Kanban"
                  aria-label="Visualização em kanban"
                >
                  <Iconify icon="mdi:view-column-outline" width={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('calendar')}
                  className={viewMode === 'calendar' ? P.iconButtonActive : P.iconButton}
                  title="Calendário"
                  aria-label="Visualização em calendário"
                >
                  <Iconify icon="mdi:calendar-month-outline" width={16} />
                </button>
              </div>

              <button
                type="button"
                onClick={() => setAlertasOpen(true)}
                className={`${P.iconButton} relative`}
                title="Como funcionam os alertas"
                aria-label="Como funcionam os alertas"
              >
                <Iconify icon="mdi:bell-outline" width={18} />
                {permissaoNotificacao !== 'granted' && permissaoNotificacao !== 'unsupported' && (
                  <span className={AT.triggerDot} />
                )}
              </button>

              <button type="button" onClick={handleNovaTarefa} className={P.primaryButton}>
                <Iconify icon="eva:plus-fill" width={16} />
                Nova Tarefa
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={P.body}>
        <div className={P.controlBar}>
          {workflows.length > 0 && (
            <div className={P.filterField}>
              <Select value={selectedWorkflowId} onValueChange={(v) => setSelectedWorkflowId(v)}>
                <SelectTrigger className={P.filterSelect}>
                  <SelectValue placeholder="Workflow" />
                </SelectTrigger>
                <SelectContent>
                  {workflows.map((w) => (
                    <SelectItem key={w._id} value={w._id}>
                      {w.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {['admin', 'root', 'supervisor'].includes(user?.level) && (
            <div className={P.filterField}>
              <Select value={filterResponsavel} onValueChange={(v) => setFilterResponsavel(v)}>
                <SelectTrigger className={P.filterSelect}>
                  <SelectValue placeholder="Responsável" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {usuariosList.map((u) => (
                    <SelectItem key={u._id} value={u._id}>
                      {u.nomeFantasia || u.nome || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className={P.filterField}>
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v)}>
              <SelectTrigger className={P.filterSelect}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {sortedEstagios.map((e) => (
                  <SelectItem key={e.nome} value={e.nome}>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.cor }} />
                      {e.nome}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className={P.filterFieldSm}>
            <Select value={filterPrioridade} onValueChange={(v) => setFilterPrioridade(v)}>
              <SelectTrigger className={P.filterSelect}>
                <SelectValue placeholder="Prioridade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="low">Baixa</SelectItem>
                <SelectItem value="medium">Média</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="urgent">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {activeFilters.length > 0 && (
            <button type="button" onClick={clearAllFilters} className={P.ghostButton}>
              Limpar filtros
            </button>
          )}
        </div>

          {avisoAlertas && (
            <div className={AT.strip}>
              <Iconify icon="mdi:bell-outline" width={18} className={AT.stripIcon} />
              <span className={AT.stripText}>
                As tarefas avisam quem precisa saber — responsável, participantes e lembrete de prazo.
              </span>
              <button type="button" onClick={() => setAlertasOpen(true)} className={AT.stripLink}>
                Ver como funciona
              </button>
              <button
                type="button"
                onClick={dispensarAvisoAlertas}
                className={AT.stripClose}
                aria-label="Dispensar aviso"
              >
                <Iconify icon="eva:close-outline" width={16} />
              </button>
            </div>
          )}
          {loading ? (
            <div className={P.card}>
              <div className={P.emptyState}>
                <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#7C3AED]" />
                <p className={P.emptyDesc}>Carregando tarefas...</p>
              </div>
            </div>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard
            tasks={filteredTasks}
            onStatusChange={handleStatusChange}
            onUpdate={handleTaskUpdate}
            onEdit={handleEdit}
            workflow={selectedWorkflow}
            usuariosList={usuariosList}
          />
          ) : viewMode === 'calendar' ? (
          <CalendarView
            tasks={filteredTasks}
            sortedEstagios={sortedEstagios}
            onEdit={handleEdit}
            onNewTask={handleNovaTarefaNaData}
          />
          ) : filteredTasks.length === 0 ? (
            <div className={P.card}>
              <div className={P.emptyState}>
                <div className={P.emptyIcon}>
                  <Iconify icon="mdi:clipboard-text-outline" width={28} />
                </div>
                <p className={P.emptyTitle}>Nenhuma tarefa encontrada</p>
                <p className={P.emptyDesc}>
                  {activeFilters.length > 0
                    ? 'Nenhuma tarefa corresponde aos filtros aplicados.'
                    : 'Você ainda não criou nenhuma tarefa.'}
                </p>
                {activeFilters.length > 0 ? (
                  <button type="button" className={`${P.link} mt-4`} onClick={clearAllFilters}>
                    Limpar filtros
                  </button>
                ) : (
                  <button type="button" className={`${P.link} mt-4`} onClick={handleNovaTarefa}>
                    Criar tarefa agora
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className={P.card}>
              <div className="p-3">
                <Table>
                <TableHeader className="bg-gray-50/80 dark:bg-[#1A1D21]/50">
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={allCurrentPageSelected}
                        onCheckedChange={toggleSelectAll}
                        className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                      />
                    </TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Tarefas</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Status</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Prazo</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Responsável</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Criado por</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Prioridade</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Workflow</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTasks.map((task) => {
                    const priorityCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                    const isSelected = selectedIds.includes(task._id);
                    const workflowName = task.workflow_id?.nome || selectedWorkflow?.nome || '-';
                    return (
                      <TableRow
                        key={task._id}
                        className={`group hover:bg-gray-50/50 dark:hover:bg-[#383838]/20 transition-colors border-b border-gray-50 dark:border-[#383838]/50 ${isSelected ? 'bg-[#7C3AED]/5' : ''}`}
                      >
                        <TableCell className="w-12" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(task._id)}
                            className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                          />
                        </TableCell>
                        <TableCell className="cursor-pointer" onClick={() => handleEdit(task)}>
                          <div className="flex items-center gap-2.5">
                            <Iconify icon="mdi:clipboard-check-outline" width={16} className="text-[#7C3AED] flex-shrink-0" />
                            <span className="text-sm font-medium text-[#7C3AED] hover:underline">
                              {task.title || 'Sem título'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {renderStatusBadge(task)}
                        </TableCell>
                        <TableCell>
                          {task.due_date ? (
                            <span className={`text-sm ${isOverdue(task) ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-600 dark:text-[#ABABAD]'}`}>
                              {formatDateTime(task.due_date)}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Sem prazo</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const assignedId = task.assigned_to?._id || task.assigned_to;
                            const populatedObj = typeof task.assigned_to === 'object' ? task.assigned_to : null;
                            const listUser = usuariosList.find(u => u._id === assignedId);
                            const displayText = populatedObj?.nomeFantasia || listUser?.nomeFantasia || populatedObj?.nome || listUser?.nome || populatedObj?.email || listUser?.email;
                            if (displayText) {
                              return (
                                <span className="text-sm text-gray-600 dark:text-[#D1D2D3] truncate max-w-[150px] inline-block" title={displayText}>
                                  {displayText}
                                </span>
                              );
                            }
                            return <span className="text-sm text-gray-400 italic">-</span>;
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const createdById = task.created_by?._id || task.created_by;
                            const populatedCreator = typeof task.created_by === 'object' ? task.created_by : null;
                            const listCreator = usuariosList.find(u => u._id === createdById);
                            const creatorName = populatedCreator?.nomeFantasia || listCreator?.nomeFantasia || populatedCreator?.nome || listCreator?.nome || populatedCreator?.email || listCreator?.email;
                            if (creatorName) {
                              return (
                                <span className="text-sm text-gray-500 dark:text-[#ABABAD] truncate max-w-[150px] inline-block" title={creatorName}>
                                  {creatorName}
                                </span>
                              );
                            }
                            return <span className="text-sm text-gray-400 italic">-</span>;
                          })()}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${priorityCfg.bg} ${priorityCfg.text}`}>
                            {priorityCfg.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600 dark:text-[#ABABAD]">{workflowName}</span>
                        </TableCell>
                        <TableCell className="w-10" onClick={e => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-lg text-gray-400 dark:text-[#ABABAD] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 opacity-0 group-hover:opacity-100 transition-all"
                            onClick={() => handleEdit(task)}
                            title="Ver detalhes"
                          >
                            <Iconify icon="mdi:information-outline" width={18} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-[#383838]">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-[#ABABAD]">
                <span>Exibindo</span>
                <Select value={String(perPage)} onValueChange={v => { setPerPage(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-[65px] h-8 rounded-lg border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] text-sm dark:text-[#D1D2D3]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span>de {filteredTasks.length} tarefas</span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm text-gray-500 dark:text-[#ABABAD] hover:text-gray-900 dark:hover:text-[#D1D2D3] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                {(() => {
                  const windowSize = 5;
                  const half = Math.floor(windowSize / 2);
                  let start = Math.max(1, page - half);
                  let end = Math.min(totalPages, start + windowSize - 1);
                  start = Math.max(1, end - windowSize + 1);
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                })().map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${p === page ? 'bg-[#7C3AED]/10 text-[#7C3AED] font-bold' : 'text-gray-500 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#383838]'}`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm text-gray-500 dark:text-[#ABABAD] hover:text-gray-900 dark:hover:text-[#D1D2D3] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Create/Edit Dialog */}
      <TarefaDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingTask(null); setNewTaskDueDate(null); }}
        defaultDueDate={newTaskDueDate}
        onSave={handleSave}
        onDelete={(task) => { setDialogOpen(false); setEditingTask(null); setTaskToDelete(task); setDeleteDialogOpen(true); }}
        task={editingTask}
        workflows={workflows}
        selectedWorkflowId={selectedWorkflowId}
        onExplicarAlertas={() => setAlertasOpen(true)}
      />

      <AlertasTarefasDialog open={alertasOpen} onClose={() => setAlertasOpen(false)} />

      {/* No Workflow Dialog */}
      <Dialog open={noWorkflowDialogOpen} onOpenChange={(open) => !creatingDefaultWorkflow && setNoWorkflowDialogOpen(open)}>
        <DialogContent className="sm:max-w-md rounded-2xl border-0 shadow-2xl dark:bg-[#222529]">
          <DialogHeader className="pt-6 px-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#7C3AED]/10">
                <Iconify icon="mdi:alert-circle-outline" width={24} className="text-[#7C3AED]" />
              </div>
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-[#D1D2D3]">Workflow necessário</DialogTitle>
            </div>
            <DialogDescription className="text-gray-500 dark:text-[#ABABAD] pt-2">
              Antes de criar tarefas, é necessário ter um workflow configurado. Ao continuar, criaremos um workflow padrão automaticamente que você poderá personalizar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="p-6 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setNoWorkflowDialogOpen(false)}
              disabled={creatingDefaultWorkflow}
              className="rounded-xl border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3]"
            >
              Fechar
            </Button>
            <Button
              disabled={creatingDefaultWorkflow}
              onClick={async () => {
                try {
                  setCreatingDefaultWorkflow(true);
                  await actionCriarWorkflow({
                    cliente_id: user?.clienteId,
                    nome: 'Workflow Padrão',
                    descricao: 'Workflow criado automaticamente. Personalize os estágios conforme sua necessidade.',
                    estagios: [
                      { nome: 'A fazer', cor: '#6b7280', ordem: 0 },
                      { nome: 'Em andamento', cor: '#3b82f6', ordem: 1 },
                      { nome: 'Concluído', cor: '#22c55e', ordem: 2 },
                    ],
                  });
                  setNoWorkflowDialogOpen(false);
                  toast({
                    title: "Workflow padrão criado",
                    description: "Um workflow padrão foi criado automaticamente. Personalize os estágios da forma que preferir.",
                  });
                  navigate('/dashboard/configuracoes/tarefas-config');
                } catch (error) {
                  console.error('Erro ao criar workflow padrão:', error);
                  toast({ title: "Erro", description: "Não foi possível criar o workflow padrão", variant: "destructive" });
                } finally {
                  setCreatingDefaultWorkflow(false);
                }
              }}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-lg shadow-[#7C3AED]/20"
            >
              {creatingDefaultWorkflow ? (
                <><Iconify icon="mdi:loading" className="animate-spin mr-2" width={16} /> Criando...</>
              ) : (
                'Criar e Configurar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl border-0 shadow-2xl dark:bg-[#222529]">
          <DialogHeader className="pt-6 px-6">
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-[#D1D2D3]">Confirmar exclusão</DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-[#ABABAD] pt-2">
              Tem certeza que deseja excluir a tarefa <span className="font-semibold text-gray-900 dark:text-[#D1D2D3]">"{taskToDelete?.title}"</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="p-6 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="rounded-xl border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-lg shadow-red-600/20"
            >
              Excluir Tarefa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
